import { useEffect, useRef, useState, useCallback } from 'react'
import io from 'socket.io-client'
import { playNotificationSound } from '../utils/notificationSound'
import { apiUrl } from '../common/config/apiUrl'

const SOCKET_URL = 'https://hangernodeapi.cloudshosting.co.uk'

export const useAgentSocket = (token) => {
  const socketRef = useRef(null)
  const [isConnected, setIsConnected] = useState(false)
  const [newConversations, setNewConversations] = useState([])
  const [incomingMessages, setIncomingMessages] = useState([])
  const [typingUsers, setTypingUsers] = useState({}) // { conversationId: { isTyping: true, userName: 'Customer' } }
  const [pendingConversations, setPendingConversations] = useState([])
  const [statusChanges, setStatusChanges] = useState([])
  const [user, setUser] = useState({});

  /*
   * Agent Status Management
   * Requirement: Persist status on refresh (Session Storage), reset on new session.
   */
  const [agentStatus, setAgentStatus] = useState(() => {
    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem('agent_status') || 'online'
    }
    return 'online'
  })

  useEffect(() => {
    if (!token) {
      console.log('No token provided, skipping socket connection')
      return
    }

    console.log('Connecting to agent socket...')

    // Connect to agent namespace
    const socket = io(`${SOCKET_URL}/agent`, {
      auth: { token },
      transports: ['websocket', 'polling']
    })

    socket.on('connect', () => {
      console.log('✅ Agent socket connected:', socket.id)
      setIsConnected(true)

      // Sync backend with our persisted session status
      // This handles the "Refresh" case where we want to stay "Away" or "Offline"
      const currentStatus = agentStatusRef.current
      console.log('🔄 Syncing persisted status to backend:', currentStatus)
      socket.emit('agent:status_change', { status: currentStatus })
    })

    socket.on('disconnect', () => {
      console.log('❌ Agent socket disconnected')
      setIsConnected(false)
    })

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message)
      setIsConnected(false)
    })

    // New conversation assigned
    socket.on('conversation:assigned', (data) => {
      // Filter out if offline
      if (agentStatusRef.current === 'offline') {
        console.log('⛔ Ignoring conversation assignment (Agent is Offline)')
        return
      }
      console.log('📩 New conversation assigned:', data)
      setNewConversations(prev => [data.conversation, ...prev])
    })

    // Conversation queued
    socket.on('conversation:queued', (data) => {
      // Filter out if offline
      if (agentStatusRef.current === 'offline') {
        console.log('⛔ Ignoring queued conversation (Agent is Offline)')
        return
      }
      console.log('⏳ Conversation queued:', data)
      setNewConversations(prev => [data.conversation, ...prev])
    })

    // Message received
    socket.on('message:received', (data) => {
      console.log('📨 message:received - Full data:', data)

      // CRITICAL: Filter out if offline
      // Using ref to get latest status inside closure
      if (agentStatusRef.current === 'offline') {
        console.log('⛔ Ignoring incoming message (Agent is Offline)')
        return
      }

      if (data) {
        if (typeof localStorage !== 'undefined') {
          let getUser = JSON.parse(localStorage.getItem('user'))
          setUser(getUser)

          // Extract assigned_agent_id from possible locations in the data structure
          const assignedAgentId = data?.assigned_agent_id
            || data?.conversation?.assigned_agent_id
            || data?.conversation?.assignedAgent?.id

          // Check if message is from customer
          const senderType = data?.message?.sender_type || data?.sender_type
          const isCustomerMessage = senderType === 'customer'

          console.log('🔍 Message filtering:', {
            currentUserId: getUser?.id,
            assignedAgentId: assignedAgentId,
            conversationStatus: data?.conversation?.status,
            senderType: senderType,
            conversationId: data?.conversation_id || data?.conversation?.id,
            isCustomerMessage: isCustomerMessage
          })

          // Show messages in two scenarios:
          // 1. Pending/unassigned conversations (assignedAgentId is null) → Show to ALL agents
          // 2. Assigned conversations → Show ONLY to the assigned agent
          if (!assignedAgentId) {
            // Pending conversation - show to all agents for queue visibility
            console.log('✅ Pending conversation message - showing to all agents')
            setIncomingMessages(prev => [...prev, data])
            // Play notification sound for customer messages
            if (isCustomerMessage) {
              console.log('🔊 Attempting to play notification sound for pending conversation')
              const customerName = data?.conversation?.customer_name || 'A customer';
              playNotificationSound(data?.conversation_id, customerName)
            } else {
              console.log('⏭️ Skipping sound - not a customer message')
            }
          } else if (getUser?.id && getUser.id === assignedAgentId) {
            // Assigned to this agent - show only to this agent
            console.log('✅ Message is for this agent, adding to incoming messages')
            setIncomingMessages(prev => [...prev, data])
            // Play notification sound for customer messages
            if (isCustomerMessage) {
              console.log('🔊 Attempting to play notification sound for assigned conversation')
              const customerName = data?.conversation?.customer_name || 'A customer';
              playNotificationSound(data?.conversation_id, customerName)
            } else {
              console.log('⏭️ Skipping sound - not a customer message')
            }
          } else {
            // Assigned to different agent - don't show
            console.log('❌ Message filtered out - assigned to different agent')
          }
        }
      }
    })

    // Customer typing
    socket.on('customer:typing', (data) => {
      // Filter out if offline
      if (agentStatusRef.current === 'offline') return

      console.log('✍️ Customer typing:', data)
      const { conversation_id, isTyping, customer_name } = data

      setTypingUsers(prev => {
        if (isTyping) {
          return {
            ...prev,
            [conversation_id]: {
              isTyping: true,
              userName: customer_name || 'Customer'
            }
          }
        } else {
          const updated = { ...prev }
          delete updated[conversation_id]
          return updated
        }
      })
    })

    // Customer disconnected
    socket.on('customer:disconnected', (data) => {
      console.log('👋 Customer disconnected:', data)
    })

    // Agent status updated
    socket.on('agent:status_updated', (data) => {
      console.log('👤 Agent status updated:', data)
      if (data && data.status) {
        setAgentStatus(data.status)
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('agent_status', data.status)
        }
      }
    })

    // Internal note added
    socket.on('internal_note:added', (data) => {
      if (agentStatusRef.current === 'offline') return
      console.log('📝 Internal note added:', data)
    })

    // Conversation pending (customer sent message to unassigned conversation)
    socket.on('conversation:pending', (data) => {
      if (agentStatusRef.current === 'offline') {
        console.log('⛔ Ignoring pending conversation (Agent is Offline)')
        return
      }
      console.log('🔔 Conversation pending:', data)
      setPendingConversations(prev => [data.conversation, ...prev])
    })

    // Conversation status changed (e.g., agent joined pending conversation)
    socket.on('conversation:status_changed', (data) => {
      if (agentStatusRef.current === 'offline') return
      console.log('🔄 Conversation status changed:', data)
      setStatusChanges(prev => [...prev, data])
    })

    socketRef.current = socket

    return () => {
      console.log('Disconnecting socket...')
      socket.disconnect()
    }
  }, [token]);

  // Ref to hold latest agent status for use inside socket callbacks
  const agentStatusRef = useRef(agentStatus)
  useEffect(() => {
    agentStatusRef.current = agentStatus
  }, [agentStatus])


  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      let getUser = JSON.parse(localStorage.getItem('user'))
      setUser(getUser)
    }
  }, [])

  // Send message via socket
  const sendMessage = useCallback((conversationId, message, callback) => {
    if (!socketRef.current) {
      console.error('Socket not connected')
      return
    }

    const payload = {
      conversation_id: conversationId,
      message
    }

    console.log('📤 Emitting agent:message with payload:', payload)

    socketRef.current.emit('agent:message', payload, (response) => {
      console.log('📥 Received callback response for agent:message:', response)
      if (callback) callback(response)
    })
  }, [])

  // Set typing indicator
  const setTyping = useCallback((conversationId, isTyping) => {
    if (!socketRef.current) return
    socketRef.current.emit('agent:typing', {
      conversation_id: conversationId,
      isTyping
    })
  }, [])

  // Take conversation
  const takeConversation = useCallback((conversationId, callback) => {
    if (!socketRef.current) return
    socketRef.current.emit('agent:take_conversation', {
      conversation_id: conversationId
    }, callback)
  }, [])

  // Close conversation
  const closeConversation = useCallback((conversationId, callback) => {
    if (!socketRef.current) return
    socketRef.current.emit('agent:close_conversation', {
      conversation_id: conversationId
    }, callback)
  }, [])

  // Change agent status
  const changeStatus = useCallback((status, callback) => {
    if (!socketRef.current) return

    // Optimistic update
    setAgentStatus(status)
    agentStatusRef.current = status
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('agent_status', status)
    }

    socketRef.current.emit('agent:status_change', { status }, (response) => {
      if (callback) callback(response)
      // Revert if failed (optional, but good practice)
      if (response && !response.success) {
        // Maybe revert? For now trust optimistic or user retry.
        console.warn("Status change failed on server:", response)
      }
    })
  }, [])

  // Join conversation room
  const joinConversation = useCallback((conversationId, callback) => {
    if (!socketRef.current) return
    socketRef.current.emit('agent:join_conversation', {
      conversation_id: conversationId
    }, callback)
  }, [])

  // Transfer conversation
  const transferConversation = useCallback((conversationId, toAgentId, callback) => {
    if (!socketRef.current) return
    socketRef.current.emit('agent:transfer', {
      conversation_id: conversationId,
      to_agent_id: toAgentId
    }, callback)
  }, [])

  // Add internal note
  const addInternalNote = useCallback((conversationId, note, callback) => {
    if (!socketRef.current) return
    socketRef.current.emit('agent:internal_note', {
      conversation_id: conversationId,
      note
    }, callback)
  }, [])

  // Mark messages as read
  const markAsRead = useCallback((conversationId) => {
    if (!socketRef.current) return
    socketRef.current.emit('agent:read', {
      conversation_id: conversationId
    })
  }, [])

  // Clear new conversations list
  const clearNewConversations = useCallback(() => {
    setNewConversations([])
  }, [])

  // Clear incoming messages for a conversation
  const clearIncomingMessages = useCallback(() => {
    setIncomingMessages([])
  }, [])

  // Clear pending conversations
  const clearPendingConversations = useCallback(() => {
    setPendingConversations([])
  }, [])

  // Clear status changes
  const clearStatusChanges = useCallback(() => {
    setStatusChanges([])
  }, [])

  return {
    isConnected,
    user,
    agentStatus, // Expose status
    newConversations,
    incomingMessages,
    typingUsers,
    pendingConversations,
    statusChanges,
    sendMessage,
    setTyping,
    takeConversation,
    closeConversation,
    changeStatus,
    joinConversation,
    transferConversation,
    addInternalNote,
    markAsRead,
    clearNewConversations,
    clearIncomingMessages,
    clearPendingConversations,
    clearStatusChanges
  }
}
